export const EXCLUSION_VIVIER_ID = '64e8cfd10b4f73f442432c9f';

export const toUserIdString = (id) => id?.toString?.() ?? String(id);

export const normalizeVivierNames = (viviers) =>
  (viviers || [])
    .map((vivier) => (typeof vivier === 'string' ? vivier : vivier?.name))
    .filter(Boolean);

export const mergeVivierNames = (existingNames, addedViviers) => {
  const names = new Set(normalizeVivierNames(existingNames));
  for (const vivier of addedViviers || []) {
    const name = typeof vivier === 'string' ? vivier : vivier?.name;
    if (name) names.add(name);
  }
  return [...names];
};

export const mergeVivierDetails = (existingDetails, addedViviers) => {
  const byId = new Map();

  for (const vivier of existingDetails || []) {
    const id = toUserIdString(vivier?._id);
    if (id) byId.set(id, vivier);
  }

  for (const vivier of addedViviers || []) {
    const id = toUserIdString(vivier?._id);
    if (!id || id === EXCLUSION_VIVIER_ID) continue;
    byId.set(id, vivier);
  }

  return [...byId.values()];
};

export const buildSelectedVivierStatusMap = (viviers, userId) => {
  const userIdStr = toUserIdString(userId);
  const statusMap = {};

  for (const vivier of viviers || []) {
    const vivierId = toUserIdString(vivier?._id);
    const preference =
      vivier.user_preferences?.find(
        (pref) => !pref.id_user || toUserIdString(pref.id_user) === userIdStr
      ) || vivier.user_preferences?.[0];

    if (vivierId && preference?.status) {
      statusMap[vivierId] = preference.status;
    }
  }

  return statusMap;
};

const enrichViviersAdded = (viviersAdded, userId) =>
  (viviersAdded || []).map((vivier) => ({
    ...vivier,
    user_preferences: (vivier.user_preferences || []).map((preference) => ({
      ...preference,
      id_user: preference.id_user ?? userId,
    })),
  }));

export const buildAddUserVivierPatches = ({
  userIds,
  vivierIds,
  vivierCatalog,
  vivierStatus,
}) => {
  const vivierById = new Map(
    (vivierCatalog || []).map((vivier) => [toUserIdString(vivier._id), vivier])
  );

  return (userIds || []).map((idUser) => {
    const addedViviers = (vivierIds || [])
      .map((id) => vivierById.get(toUserIdString(id)))
      .filter(Boolean)
      .filter((vivier) => toUserIdString(vivier._id) !== EXCLUSION_VIVIER_ID)
      .map((vivier) => ({
        _id: vivier._id,
        name: vivier.name,
        user_preferences: [{ id_user: idUser, status: vivierStatus }],
      }));

    return {
      id_user: idUser,
      viviersAdded: addedViviers,
      vivier_status: vivierStatus,
    };
  });
};

export const resolveVivierNamesFromPatch = (row, patch) => {
  if (patch.viviers !== undefined) return normalizeVivierNames(patch.viviers);
  if (patch.viviersDetail !== undefined) return normalizeVivierNames(patch.viviersDetail);
  if (patch.viviersAdded?.length) return mergeVivierNames(row?.viviers, patch.viviersAdded);
  return row?.viviers;
};

export const VIVIER_LIST_PATCH_FIELDS = [
  'firstname',
  'lastname',
  'email',
  'phone',
  'idBestt',
  'tokenRefreshMobile',
  'tokenRefresh',
  'validation',
  'viviersDetail',
  'preferencesDepartment',
  'licences',
  'unavailableDays',
  'availability',
  'registrationSource',
  'cv',
  'vivier_status',
];

export const applyPatchToListRow = (row, patch) => {
  if (!patch) return row;

  if (patch._deleted) return null;

  const merged = { ...row };

  if (patch.isDeleted) {
    merged.isDeleted = true;
  }

  const vivierNames = resolveVivierNamesFromPatch(row, patch);
  if (vivierNames !== undefined) {
    merged.viviers = vivierNames;
  }

  for (const field of VIVIER_LIST_PATCH_FIELDS) {
    if (patch[field] !== undefined) {
      merged[field] = patch[field];
    }
  }

  return merged;
};

export const isUserInCurrentVivier = (patch, currentVivierName) => {
  if (!currentVivierName) return true;
  if (patch?.excluded) return false;

  const names = resolveVivierNamesFromPatch({}, patch);
  return (names || []).includes(currentVivierName);
};

export const applyPatchToFicheRow = (row, patch, currentVivierName) => {
  if (!patch) return row;

  const merged = { ...row };

  if (!isUserInCurrentVivier(patch, currentVivierName)) {
    merged.isDeleted = true;
    return merged;
  }

  const vivierNames = resolveVivierNamesFromPatch(row, patch);
  if (vivierNames !== undefined) {
    merged.viviers = vivierNames;
  }

  if (patch.preferencesDepartment !== undefined) {
    merged.preferencesDepartment = patch.preferencesDepartment;
  }

  if (patch.vivier_status !== undefined) {
    merged.vivier_status = patch.vivier_status;
  }

  return merged;
};

export const applyPatchToTempWorker = (tempWorkerInfos, patch) => {
  if (!tempWorkerInfos || !patch) return tempWorkerInfos;

  const userId = tempWorkerInfos.user?._id ?? tempWorkerInfos.tempWorker?.id_user;

  if (patch.viviersDetail !== undefined) {
    return { ...tempWorkerInfos, viviers: patch.viviersDetail };
  }

  if (patch.viviersAdded?.length) {
    return {
      ...tempWorkerInfos,
      viviers: mergeVivierDetails(
        tempWorkerInfos.viviers,
        enrichViviersAdded(patch.viviersAdded, userId)
      ),
    };
  }

  // Patches carry either vivier documents (slide over) or plain names (list rows);
  // only the former may land here. An empty array is a legitimate document patch —
  // it is what the delete endpoint returns once the user leaves their last vivier.
  if (patch.viviers !== undefined && !patch.viviers.some((vivier) => typeof vivier === 'string')) {
    return { ...tempWorkerInfos, viviers: patch.viviers };
  }

  return tempWorkerInfos;
};

export const findPatchForUser = (patches, userId) =>
  (patches || []).find(
    (patch) =>
      toUserIdString(patch.id_user ?? patch._id) === toUserIdString(userId)
  );

export const isUserStillExcluded = (viviers) =>
  (viviers || []).some((vivier) => toUserIdString(vivier?._id) === EXCLUSION_VIVIER_ID);
